/**
 * Modal that previews an advance receipt PDF inline (browser native viewer),
 * with download/print/close actions. Used by both the admin AdvanceDetailPage
 * and the employee EmployeeAdvancesCard.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Download, X, Loader2, Printer } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { PdfViewer } from "./PdfViewer";

export function AdvancePdfPreviewDialog({ advance, open, onClose }) {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !advance?.id) {
            setPdfUrl(null);
            return;
        }
        let revokeUrl = null;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await api.get(`/advances/${advance.id}/pdf`, { responseType: "blob" });
                if (cancelled) return;
                const blob = new Blob([r.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                revokeUrl = url;
                setPdfUrl(url);
            } catch (e) {
                toast.error("Errore generazione anteprima PDF");
                onClose?.();
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            if (revokeUrl) {
                setTimeout(() => window.URL.revokeObjectURL(revokeUrl), 250);
            }
        };
    }, [open, advance?.id, onClose]);

    const handleDownload = () => {
        if (!pdfUrl || !advance) return;
        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = `Ricevuta_acconto_${advance.receipt_number}_${advance.receipt_year}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const handlePrint = () => {
        if (!pdfUrl) return;
        const w = window.open(pdfUrl, "_blank");
        if (w) {
            // Slight delay so the PDF can render before the print dialog opens
            setTimeout(() => {
                try { w.print(); } catch (e) { /* ignore */ }
            }, 700);
        } else {
            toast.error("Popup bloccato — abilita i popup per stampare");
        }
    };

    if (!advance) return null;

    return (
        <Dialog open={open} modal={false} onOpenChange={(v) => !v && onClose?.()}>
            {open && (
                <div
                    className="fixed inset-0 z-40 bg-black/70"
                    aria-hidden="true"
                    onClick={() => onClose?.()}
                    data-testid="advance-preview-backdrop"
                />
            )}
            <DialogContent
                className="flex h-[92vh] max-w-5xl flex-col gap-0 overflow-hidden p-0"
                data-testid="advance-pdf-preview"
            >
                <DialogTitle className="sr-only">Anteprima ricevuta acconto</DialogTitle>
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-br from-[#15498F] to-[#376DB3] px-5 py-3 text-white">
                    <div>
                        <h3 className="font-display text-base font-bold">
                            Anteprima ricevuta acconto
                        </h3>
                        <p className="text-xs opacity-90">
                            #{advance.receipt_number}/{advance.receipt_year} ·{" "}
                            {advance.type_label || advance.type} · € {Number(advance.amount || 0).toFixed(2).replace(".", ",")}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={handlePrint}
                            disabled={!pdfUrl || loading}
                            className="gap-1.5 bg-white/15 text-white hover:bg-white/25"
                            data-testid="advance-print-btn"
                        >
                            <Printer className="h-3.5 w-3.5" />
                            Stampa
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={handleDownload}
                            disabled={!pdfUrl || loading}
                            className="gap-1.5 bg-white text-[#15498F] hover:bg-white/90"
                            data-testid="advance-download-btn"
                        >
                            <Download className="h-3.5 w-3.5" />
                            Scarica PDF
                        </Button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
                            aria-label="Chiudi"
                            data-testid="advance-preview-close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* PDF viewer */}
                <div className="relative flex-1 bg-slate-100">
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                            <div className="flex flex-col items-center gap-2">
                                <Loader2 className="h-6 w-6 animate-spin text-[#15498F]" />
                                <p className="text-xs text-slate-600">Generazione anteprima…</p>
                            </div>
                        </div>
                    )}
                    {pdfUrl && (
                        <div className="absolute inset-0">
                            <PdfViewer fileUrl={pdfUrl} />
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default AdvancePdfPreviewDialog;
